"use client";

import { createContext, ReactNode, use, useEffect, useState } from "react";
import { type PricelistForMemberships } from "@/utils/getPricelistForUserMemberships";
import { Logger } from "@cappelendamm/logger/dist/logger";

type PricelistContextState = {
  applicablePricelists: PricelistForMemberships[];
};

export const PricelistContext = createContext<PricelistContextState>({
  applicablePricelists: [],
});

export function PricelistContextProvider({
  children,
  initialPricelists,
}: {
  children: ReactNode;
  initialPricelists: PricelistForMemberships[];
}) {
  const [applicablePricelists, setApplicablePricelists] =
    useState<PricelistForMemberships[]>(initialPricelists);

  useEffect(() => {
    const controller = new AbortController();

    const loadPricelists = async () => {
      try {
        const response = await fetch("/api/user-info", {
          method: "GET",
          cache: "no-store",
          credentials: "include",
          signal: controller.signal,
        });

        if (!response.ok) {
          return;
        }

        const data = (await response.json()) as {
          applicablePricelists?: PricelistForMemberships[];
        };

        setApplicablePricelists(
          Array.isArray(data.applicablePricelists)
            ? data.applicablePricelists
            : [],
        );
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") {
          return;
        }

        Logger.error("Failed to fetch applicable pricelists", error);
      }
    };

    void loadPricelists();

    return () => {
      controller.abort();
    };
  }, []);

  return (
    <PricelistContext value={{ applicablePricelists }}>
      {children}
    </PricelistContext>
  );
}

export function usePricelist() {
  return use(PricelistContext);
}
